'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { tutorialSteps, getNextStep, getPreviousStep, getTutorialProgress } from '@/lib/tutorial/tutorialSteps';
import type { TutorialStep } from '@/lib/tutorial/tutorialSteps';

interface TutorialContextType {
  isActive: boolean;
  currentStep: TutorialStep | null;
  currentStepIndex: number;
  totalSteps: number;
  progress: number;
  startTutorial: () => void;
  dismissTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepId: string) => void;
  completeTutorial: () => void;
  resetTutorial: () => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

const TUTORIAL_STORAGE_KEY = 'onelastfarewell_tutorial_state';

interface TutorialState {
  isActive: boolean;
  currentStepId: string | null;
  isCompleted: boolean;
  isDismissed: boolean;
}

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // Load tutorial state from localStorage on mount
  useEffect(() => {
    setIsMounted(true);
    const savedState = localStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (savedState) {
      try {
        const state: TutorialState = JSON.parse(savedState);
        if (!state.isCompleted && !state.isDismissed) {
          setIsActive(state.isActive);
          setCurrentStepId(state.currentStepId);
        }
        setIsCompleted(state.isCompleted);
      } catch (error) {
        console.error('Failed to parse tutorial state:', error);
      }
    }
  }, []);

  // Save tutorial state to localStorage whenever it changes
  useEffect(() => {
    if (isMounted) {
      const state: TutorialState = {
        isActive,
        currentStepId,
        isCompleted,
        isDismissed: !isActive && !isCompleted,
      };
      localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(state));
    }
  }, [isActive, currentStepId, isCompleted, isMounted]);

  const currentStep = currentStepId
    ? tutorialSteps.find(step => step.id === currentStepId) || null
    : null;

  const currentStepIndex = currentStepId
    ? tutorialSteps.findIndex(step => step.id === currentStepId)
    : -1;

  const progress = currentStepId ? getTutorialProgress(currentStepId) : 0;

  const startTutorial = () => {
    setIsActive(true);
    setCurrentStepId(tutorialSteps[0].id);
    setIsCompleted(false);
  };

  const dismissTutorial = () => {
    setIsActive(false);
  };

  const nextStep = () => {
    if (!currentStepId) return;
    
    const next = getNextStep(currentStepId);
    if (next) {
      setCurrentStepId(next.id);
    } else {
      // Last step reached
      completeTutorial();
    }
  };

  const previousStep = () => {
    if (!currentStepId) return;
    
    const previous = getPreviousStep(currentStepId);
    if (previous) {
      setCurrentStepId(previous.id);
    }
  };

  const goToStep = (stepId: string) => {
    const step = tutorialSteps.find(s => s.id === stepId);
    if (step) {
      setCurrentStepId(stepId);
      setIsActive(true);
    }
  };

  const completeTutorial = () => {
    setIsActive(false);
    setIsCompleted(true);
    setCurrentStepId(null);
  };

  const resetTutorial = () => {
    setIsActive(false);
    setIsCompleted(false);
    setCurrentStepId(null);
    localStorage.removeItem(TUTORIAL_STORAGE_KEY);
  };

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        currentStepIndex,
        totalSteps: tutorialSteps.length,
        progress,
        startTutorial,
        dismissTutorial,
        nextStep,
        previousStep,
        goToStep,
        completeTutorial,
        resetTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
}